import React, { useEffect, useState } from 'react';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, EditOutlined, DeleteFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Pagination,
  Popconfirm,
  ConfigProvider,
  BackTop,
} from 'antd';
import { Table, Space, Tooltip, Button } from 'antd';
import TableDateRow from 'modules/layoutModule/components/TableDateRow/TableDateRow';
import plPL from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import TableDataRowTypes from 'modules/layoutModule/enums/TableDataRowTypes';
import useColumnSearch from 'modules/layoutModule/hooks/useColumnSearch';
import {
  getFertilizersCategoriesRequest,
  removeSpecificCategoryRequest,
} from '../requests/fertilizersRequests';

export interface NewsTableTypes {
  id: number;
  dateAdd: moment.MomentInput;
  name: string;
  isActive: number;
  activitySwitchDisabled?: boolean;
}

const FertilizersCategories = (): JSX.Element => {
  const [tableData, setTableData] = useState<NewsTableTypes[]>([]);
  const [loading, toggleLoading] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState('');

  const confirmDelete = (id: number) => {
    removeSpecificCategoryRequest(id).then(() => {
      downloadList();
    });
  };

  const downloadList = () =>
    getFertilizersCategoriesRequest().then((response) => {
      if (response) {
        const { data } = response;
        setTableData(data);
        toggleLoading(false);
      } else {
        toggleLoading(false);
      }
    });

  useEffect(() => {
    if (tableData.length === 0) {
      downloadList();
    }
  }, [tableData]);

  const columns = [
    {
      title: 'Kategoria główna nawozów',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
      ...useColumnSearch(
        'name',
        'Szukaj po tytule',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn
      ),
    },
    {
      title: 'Podkategoria nawozów',
      dataIndex: 'subCatName',
      width: 300,
      sorter: (a: { subCatName:string }, b: { subCatName:string }) =>
        a.subCatName.localeCompare(b.subCatName),
    },
    {
      title: 'Kolor nawozów',
      dataIndex: 'color',
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.COLOUR} />
      ),
    },
    {
      title: 'Aktywna',
      dataIndex: 'isActive',
      key: 'isActive',
      width: '150px',
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IS_ACTIVE} />
      ),
      sorter: (a: { isActive: number }, b: { isActive: number }) =>
        a.isActive - b.isActive,
      filters: [
        {
          text: 'TAK',
          value: 'isActive',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isActive: boolean }) => {
        if (value === 'isActive') {
          return record.isActive;
        }
        return !record.isActive;
      },
    },
    {
      title: 'Kolejność',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
      sorter: (a: any, b: any) => a.sortOrder - b.sortOrder,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.SORT} />
      ),
    },
    {
      title: 'Akcje',
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (text: string, item: NewsTableTypes) => (
        <Space
          size="middle"
          style={{ display: 'Flex', flexDirection: 'column' }}
        >
          <Tooltip title="Edytuj wpis">
            <Link to={`/fertilizers/categories/${item.id}`}>
              <Button type="primary" shape="circle" icon={<EditOutlined />} />
            </Link>
          </Tooltip>
          <Tooltip title="Usuń wpis">
            <Popconfirm
              title="Czy na pewno chcesz usunąc ten wpis?"
              placement="left"
              onConfirm={() => confirmDelete(item.id)}
              okText="Tak"
              cancelText="Nie"
              disabled={item.isActive === 1 || item.activitySwitchDisabled}
            >
              <Button
                type="link"
                disabled={item.isActive === 1 || item.activitySwitchDisabled}
              >
                <DeleteFilled />
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Ustawienia treści</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/fertilizers/categories">Nawozy</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <Table
          // @ts-ignore
          columns={columns}
          // @ts-ignore
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record.id}
        >
          <Pagination />
        </Table>
        <BackTop />
      </ConfigProvider>
    </>
  );
};

export default FertilizersCategories;
